import { Link } from 'react-router-dom';
import {
  EnvelopeSimple,
  MapPin,
  InstagramLogo,
  LinkedinLogo,
  TwitterLogo,
  WhatsappLogo,
  ArrowLeft,
} from '@phosphor-icons/react';
import { WHATSAPP_URL } from './FloatingWhatsApp';
import { LEGAL_LINKS } from '../data/legal';
import NewsletterForm from './NewsletterForm';

const QUICK_LINKS = [
  { label: 'الرئيسية', to: '/' },
  { label: 'من نحن', to: '/about' },
  { label: 'البرامج المهنية', to: '/programs' },
  { label: 'بنك الكفاءات التربوية', to: '/talent-bank' },
  { label: 'تواصل معنا', to: '/contact' },
];

const CONSULTING_SERVICES = [
  { label: 'الاستشارات التربوية وتطوير تجربة الطفل', to: '/#services' },
  { label: 'الاستشارات الإدارية', to: '/#services' },
  { label: 'الجودة والتطوير', to: '/#services' },
  { label: 'استشارات تأسيس وتطوير الحضانات ورياض الأطفال', to: '/sectors/workplace-nurseries' },
  { label: 'تطوير السياسات والإجراءات', to: '/#services' },
];

const PROFESSIONAL_RESOURCES = [
  { label: 'القوالب الإدارية', to: '/#templates' },
  { label: 'القوالب التربوية', to: '/#templates' },
  { label: 'قوالب الجودة', to: '/#templates' },
  { label: 'قوالب التشغيل', to: '/#templates' },
  { label: 'قوالب الموارد البشرية', to: '/#templates' },
  { label: 'القوائم المرجعية', to: '/#templates' },
];

const SOCIALS = [
  { Icon: InstagramLogo, href: 'https://www.instagram.com/childhoodhorizons/', label: 'Instagram' },
  { Icon: LinkedinLogo, href: '#', label: 'LinkedIn' },
  { Icon: TwitterLogo, href: '#', label: 'X / Twitter' },
];

const Column = ({ title, items }) => (
  <div>
    <h4 className="text-base font-semibold font-['IBM_Plex_Sans_Arabic'] mb-5 text-white tracking-tight">
      {title}
    </h4>
    <ul className="space-y-3">
      {items.map((it) => (
        <li key={it.label}>
          <Link
            to={it.to}
            className="text-[13px] text-[#C5CDB7] hover:text-white transition-colors inline-flex items-center gap-1.5 group"
          >
            <ArrowLeft size={11} className="rtl-flip opacity-0 -translate-x-1 group-hover:opacity-60 group-hover:translate-x-0 transition-all" />
            {it.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => (
  <footer
    data-testid="footer"
    dir="rtl"
    className="bg-[#1A2A12] text-[#FAFAF7] pt-16 sm:pt-20 pb-8"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      {/* Top: Brand + 4 columns */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10 lg:gap-12 mb-12 sm:mb-16">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-3.5 mb-5">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FAFAF7] to-[#E9E4D4] flex items-center justify-center shadow-[0_6px_18px_-6px_rgba(0,0,0,0.5)] ring-1 ring-white/20 shrink-0">
              <span className="text-[#1F3D2B] font-bold text-[19px] font-['Playfair_Display'] italic leading-none">CH</span>
            </div>
            <div className="flex flex-col leading-none">
              <span
                className="font-['Playfair_Display'] font-semibold text-white text-[26px] leading-[1] tracking-[-0.015em]"
              >
                Child Horizons
              </span>
              <span className="font-['IBM_Plex_Sans_Arabic'] text-[#A8B596] text-[14px] leading-[1.2] mt-[3px] tracking-[-0.01em] font-medium">
                آفاق الطفولة
              </span>
            </div>
          </div>
          <p className="text-sm text-[#B9C2AB] leading-8 mb-6 max-w-md">
            بيت خبرة سعودي متخصص في قطاع الطفولة المبكرة، يقدم الاستشارات
            الإدارية وحلول الجودة والتطوير المؤسسي وفق أفضل الممارسات الدولية
            وبما يتوافق مع السياق المهني في المملكة.
          </p>

          <div className="flex gap-2.5">
            {SOCIALS.map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 rounded-xl bg-[#243B19] hover:bg-[#4A641F] flex items-center justify-center transition-colors"
              >
                <Icon size={18} weight="fill" />
              </a>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp"
              aria-label="WhatsApp"
              className="w-10 h-10 rounded-xl bg-[#243B19] hover:bg-[#25D366] flex items-center justify-center transition-colors"
            >
              <WhatsappLogo size={18} weight="fill" />
            </a>
          </div>
        </div>

        <Column title="روابط سريعة" items={QUICK_LINKS} />
        <Column title="الخدمات الاستشارية" items={CONSULTING_SERVICES} />
        <Column title="الموارد المهنية" items={PROFESSIONAL_RESOURCES} />
      </div>

      {/* Newsletter strip */}
      <div className="border-t border-[#2B3F1F] pt-8 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6 lg:gap-10 items-center">
          <div>
            <p className="text-[11px] tracking-[0.2em] text-[#7E916A] uppercase mb-2">
              النشرة البريدية
            </p>
            <h4 className="text-lg sm:text-xl font-bold text-white font-['IBM_Plex_Sans_Arabic'] mb-1.5">
              ابقَ ضمن مجتمع Childhood Horizons المهني
            </h4>
            <p className="text-sm text-[#B9C2AB] leading-8">
              مقال شهري مختار، أدوات مهنية جديدة، ومستجدات مركز المعرفة —
              مباشرة إلى بريدك.
            </p>
          </div>
          <div>
            <NewsletterForm variant="dark" source="footer" compact />
          </div>
        </div>
      </div>

      {/* Contact strip */}
      <div className="border-t border-[#2B3F1F] pt-8 mb-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 group"
        >
          <div className="w-10 h-10 rounded-xl bg-[#243B19] group-hover:bg-[#25D366] flex items-center justify-center transition-colors">
            <WhatsappLogo size={18} weight="fill" />
          </div>
          <div>
            <p className="text-[11px] text-[#7E916A]">واتساب</p>
            <p className="text-sm text-[#D5D1C8] group-hover:text-white transition-colors">
              تواصل عبر واتساب
            </p>
          </div>
        </a>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#243B19] flex items-center justify-center">
            <EnvelopeSimple size={18} weight="duotone" />
          </div>
          <div>
            <p className="text-[11px] text-[#7E916A]">البريد الإلكتروني</p>
            <p className="text-sm text-[#D5D1C8]" dir="ltr">
              info@childhoodhorizons.com
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#243B19] flex items-center justify-center">
            <MapPin size={18} weight="duotone" />
          </div>
          <div>
            <p className="text-[11px] text-[#7E916A]">الموقع</p>
            <p className="text-sm text-[#D5D1C8]">المملكة العربية السعودية</p>
          </div>
        </div>
      </div>

      {/* Legal links strip */}
      <div className="border-t border-[#2B3F1F] pt-6 pb-2" data-testid="footer-legal-links">
        <ul className="flex flex-wrap gap-x-5 gap-y-2.5 text-xs sm:text-[13px]">
          {LEGAL_LINKS.map((l) => (
            <li key={l.slug}>
              <Link
                to={`/legal/${l.slug}`}
                data-testid={`footer-legal-${l.slug}`}
                className="text-[#B9C2AB] hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Bottom strip */}
      <div className="border-t border-[#2B3F1F] mt-6 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
        <p className="text-xs text-[#7E916A]">
          © {new Date().getFullYear()} Child Horizons — آفاق الطفولة. جميع الحقوق محفوظة. المحتوى محمي بموجب قوانين الملكية الفكرية.
        </p>
        <p className="text-xs text-[#7E916A]" dir="ltr">
          Child Horizons — All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
